import { useEffect, useMemo, useState } from 'react';
import { Spinner } from '../ui/Spinner';
import { formatCurrency } from '../../utils/formatCurrency';
import { submitPaymentRecord } from '../../utils/lmsMutations';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';

const LINK_TYPES = new Set([
  'payhere_link',
  'stripe_link',
  'paypal_link',
  'wise_link',
  'other_link',
]);

const PayModal = ({ isOpen, onClose, paymentRequest, onSuccess }) => {
  const { methods } = usePaymentMethods('student');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    paymentMethod: 'bank_transfer',
    paymentMethodId: '',
    receiptNumber: '',
    notes: '',
  });
  const [receiptFile, setReceiptFile] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    if (!formData.paymentMethodId && methods.length) {
      const first = methods[0];
      setFormData((prev) => ({
        ...prev,
        paymentMethodId: first.id,
        paymentMethod: first.type,
      }));
    }
  }, [isOpen, methods, formData.paymentMethodId]);

  const activeMethod = useMemo(
    () => methods.find((method) => method.id === formData.paymentMethodId) || null,
    [methods, formData.paymentMethodId],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await submitPaymentRecord({
        paymentRequest,
        formData,
        receiptFile,
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting payment:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !paymentRequest) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-stone-900/45 p-4 sm:items-center">
      <div className="w-full max-w-xl rounded-[28px] border border-primary-100 bg-white shadow-xl">
        <div className="border-b border-stone-200 px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-700">
                Payment step
              </p>
              <h3 className="mt-2 text-2xl font-semibold text-stone-900">
                Send your receipt
              </h3>
              <p className="mt-2 text-sm leading-6 text-stone-600">
                Fill in the payment details below and attach your receipt image or PDF.
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-stone-400 transition hover:bg-stone-100 hover:text-stone-600"
            >
              x
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-5 py-5 sm:px-6">
          <div className="grid gap-3 rounded-[24px] bg-stone-50 p-4 text-sm text-stone-700 sm:grid-cols-2">
            <SummaryItem label="Amount" value={formatCurrency(paymentRequest?.amount)} />
            <SummaryItem
              label="Payment type"
              value={
                paymentRequest.installmentNumber
                  ? `${paymentRequest.paymentType} (${paymentRequest.installmentNumber}/${paymentRequest.totalInstallments})`
                  : paymentRequest.paymentType
              }
            />
            <SummaryItem
              label="Due date"
              value={new Date(paymentRequest.dueDate).toLocaleDateString()}
            />
            <SummaryItem label="Course" value={paymentRequest.courseName || 'Course'} />
          </div>

          <Field label="Payment method">
            <select
              className="w-full rounded-2xl border border-stone-300 px-4 py-3 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              value={formData.paymentMethodId}
              onChange={(event) => {
                const method = methods.find((m) => m.id === event.target.value);
                setFormData({
                  ...formData,
                  paymentMethodId: event.target.value,
                  paymentMethod: method?.type || event.target.value,
                });
              }}
              required
            >
              <option value="" disabled>
                Select a payment method
              </option>
              {methods.map((method) => (
                <option key={method.id} value={method.id}>
                  {method.label}
                </option>
              ))}
            </select>
          </Field>

          {activeMethod ? (
            <div className="rounded-2xl bg-stone-50 px-4 py-3 text-sm text-stone-700">
              {activeMethod.type === 'bank_account' ? (
                <ul className="space-y-0.5">
                  {activeMethod.details?.bankName ? (
                    <li>Bank: {activeMethod.details.bankName}</li>
                  ) : null}
                  {activeMethod.details?.accountName ? (
                    <li>Account name: {activeMethod.details.accountName}</li>
                  ) : null}
                  {activeMethod.details?.accountNumber ? (
                    <li>Account number: {activeMethod.details.accountNumber}</li>
                  ) : null}
                  {activeMethod.details?.branch ? (
                    <li>Branch: {activeMethod.details.branch}</li>
                  ) : null}
                  {activeMethod.details?.swiftCode ? (
                    <li>SWIFT: {activeMethod.details.swiftCode}</li>
                  ) : null}
                </ul>
              ) : null}
              {LINK_TYPES.has(activeMethod.type) && activeMethod.details?.checkoutUrl ? (
                <a
                  href={activeMethod.details.checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
                >
                  Open payment page
                </a>
              ) : null}
              {activeMethod.instructions ? (
                <p className="mt-2 text-xs text-stone-500">{activeMethod.instructions}</p>
              ) : null}
            </div>
          ) : null}

          <Field label="Receipt or reference number">
            <input
              type="text"
              className="w-full rounded-2xl border border-stone-300 px-4 py-3 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              value={formData.receiptNumber}
              onChange={(event) =>
                setFormData({ ...formData, receiptNumber: event.target.value })
              }
              required
            />
          </Field>

          <Field label="Receipt file">
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(event) => setReceiptFile(event.target.files[0])}
              className="w-full text-sm text-stone-500 file:mr-4 file:rounded-full file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:font-semibold file:text-primary-700"
            />
          </Field>

          <Field label="Short note">
            <textarea
              className="w-full rounded-2xl border border-stone-300 px-4 py-3 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              rows="3"
              value={formData.notes}
              onChange={(event) =>
                setFormData({ ...formData, notes: event.target.value })
              }
            />
          </Field>

          <div className="flex flex-col gap-3 border-t border-stone-200 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[48px] rounded-2xl border border-stone-300 px-4 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="min-h-[48px] rounded-2xl bg-primary-600 px-4 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? <Spinner size="sm" /> : 'Submit receipt'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-stone-700">{label}</span>
    {children}
  </label>
);

const SummaryItem = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
      {label}
    </p>
    <p className="mt-1 font-semibold text-stone-900">{value}</p>
  </div>
);

export default PayModal;
